import React from 'react';
import PropTypes from 'prop-types';

const RecommendedCard = ({ 
  content, 
  isMobile, 
  onClick, 
  isFavorite, 
  onToggleFavorite,
  backdropLogoUrl,
  isLoading,
  showTitleOverlay,
  year,
  extraInfo,
  ratingFormat
}) => {
  const isMovie = !!content.title;
  const type = isMovie ? 'movie' : 'series';
  
  const {
    id,
    title,
    name,
    vote_average,
    backdrop_path,
    poster_path,
    media_type
  } = content;

  const contentTitle = title || name;

  const imageSrc = isMobile 
    ? (poster_path ? `https://image.tmdb.org/t/p/w342${poster_path}` : 'https://via.placeholder.com/185x278?text=No+Poster')
    : (backdropLogoUrl || (backdrop_path ? `https://image.tmdb.org/t/p/w780${backdrop_path}` : 'https://via.placeholder.com/780x439?text=No+Backdrop'));

  const imageClass = isMobile ? 'movie-poster-mobile' : 'movie-backdrop';

  // ---- 5-star rating ----
  const createStarRating = (rating) => {
    const starRating = rating / 2;
    const fullStars = Math.floor(starRating);
    const partialStar = starRating - fullStars;
    
    let stars = [];
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div className="star" key={`full-${i}`}>
          <i className="far fa-star"></i>
          <div className="star-filled" style={{ width: '100%' }}>
            <i className="fas fa-star"></i>
          </div>
        </div>
      );
    }
    
    if (partialStar > 0) {
      stars.push(
        <div className="star" key="partial">
          <i className="far fa-star"></i>
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <i className="fas fa-star"></i>
          </div>
        </div>
      );
    }
    
    const emptyStars = 5 - Math.ceil(starRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div className="star" key={`empty-${i}`}>
          <i className="far fa-star"></i>
          <div className="star-filled" style={{ width: '0%' }}>
            <i className="fas fa-star"></i>
          </div>
        </div>
      );
    }
    
    return stars;
  };

  // ---- Single sharp star + numeric rating ----
  const formatRatingNumber = (rating) => {
    if (!rating || rating === 0) return null;
    return rating.toFixed(1);
  };

  const ratingNumber = formatRatingNumber(vote_average);

  // ---- Build meta text (no abbreviation) ----
  const buildMetaText = () => {
    let meta = '';
    if (isMovie) {
      const parts = [];
      if (year) parts.push(year);
      if (extraInfo) parts.push(extraInfo);
      meta = parts.join(' • ');
    } else {
      meta = extraInfo || '';
    }
    return meta;
  };

  const metaText = buildMetaText();

  // ---- Determine which rating component to render ----
  const renderRating = () => {
    if (ratingFormat === 'stars') {
      return (
        <div className="star-rating rating-stars">
          {createStarRating(vote_average)}
        </div>
      );
    } else {
      return (
        <div className="rating-container">
          <span className="rating-star"><i className="fa-sharp fa-solid fa-star"></i></span>
          <span className="rating-number">{ratingNumber}</span>
        </div>
      );
    }
  };

  // ---- Mobile: shorten "Seasons" to "S." for series with year range ----
  const isSeriesWithYearRange = !isMovie && year && year.includes('-');
  const shouldShortenSeason = isMobile && isSeriesWithYearRange && ratingFormat === 'stars';
  let displayMetaText = metaText;
  if (shouldShortenSeason) {
    displayMetaText = metaText.replace(/\s*Seasons?/i, ' S.');
  }
  const metaClassName = shouldShortenSeason ? 'mobile-meta mobile-meta-no-ellipsis' : 'mobile-meta';

  return (
    <div 
      className="recommended-card" 
      data-id={id} 
      data-type={media_type || type}
      onClick={(e) => {
        if (!e.target.closest('.favorite-btn')) {
          onClick(content, media_type || type);
        }
      }}
    >
      <div className="poster-container" style={{ position: 'relative', overflow: 'hidden', background: '#1a1a1a' }}>
        <img 
          src={imageSrc} 
          alt={contentTitle} 
          className={imageClass}
          loading="lazy"
        />
        {!isMobile && showTitleOverlay && (
          <div className="backdrop-title-overlay">{contentTitle.toUpperCase()}</div>
        )}
        <button 
          className={`favorite-btn ${isFavorite ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(content);
          }}
        >
          <i className={isFavorite ? "fas fa-heart" : "far fa-heart"}></i>
        </button>
      </div>
      <div className="movie-details">
        <div className="movie-title">{contentTitle.toUpperCase()}</div>
        <div className="movie-meta-row">
          {isMobile ? (
            <>
              <div className="rating-wrapper mobile-rating-wrapper">
                {renderRating()}
              </div>
              <span className={metaClassName}>{displayMetaText}</span>
            </>
          ) : (
            <>
              <div className="rating-wrapper">
                {renderRating()}
              </div>
              {metaText && (
                <div className="meta-info">{metaText}</div>
              )}
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .backdrop-title-overlay {
          position: absolute;
          top: 50%;
          left: 20px;
          transform: translateY(-50%);
          color: #ffffff;
          font-weight: 700;
          font-size: 1.4rem;
          text-shadow: 0 4px 12px rgba(0,0,0,0.8);
          pointer-events: none;
          z-index: 2;
          text-align: left;
          max-width: 70%;
          line-height: 1.2;
          transition: transform 0.5s ease;
        }
        .recommended-card:hover .backdrop-title-overlay {
          transform: translateY(-50%) scale(1.05);
        }

        .movie-details {
          padding: 8px 0px !important;
          text-align: left !important;
        }

        .movie-title {
          font-weight: 600;
          font-size: clamp(12px, 1.1vw, 16px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #fff;
          letter-spacing: 0.3px;
        }

        .movie-meta-row {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          width: 100%;
          gap: 6px;
        }

        .rating-wrapper {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          margin: 0;
          padding: 0;
        }

        .star-rating {
          display: flex;
          align-items: center;
          gap: 2px;
          margin: 0;
          padding: 0;
        }

        .star {
          position: relative;
          display: inline-block;
          font-size: clamp(10px, 0.8vw, 14px);
          color: #555;
          line-height: 1;
          margin: 0;
          padding: 0;
        }
        .star i {
          display: inline-block;
        }
        .star .star-filled {
          position: absolute;
          top: 0;
          left: 0;
          overflow: hidden;
          color: #59f001;
        }
        .star .far.fa-star {
          color: #444;
        }

        .rating-container {
          display: flex;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
          margin-left: 2px;
        }

        .rating-star {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: #59f001;
          line-height: 1;
        }
        .rating-star i {
          display: inline-block;
        }

        .rating-number {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: #59f001;
          font-weight: 600;
        }

        .meta-info {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: rgba(255, 255, 255, 0.7);
          white-space: nowrap;
          overflow: hidden;
          flex: 1 1 auto;
          min-width: 0;
          text-align: right;
        }

        /* === MOBILE === */
        @media (max-width: 768px) {
          .movie-details {
            padding: 4px 0px !important;
          }
          .movie-title {
            font-size: 11px;
          }


          
          .movie-meta-row {
            padding-left: 0 !important;
            gap: 2px !important;  /* REDUCED GAP – meta gets closer to stars */
          }
          .rating-wrapper {
            margin-left: 3px !important;
            padding: 0 !important;
          }
          .mobile-rating-wrapper .rating-stars {
            transform: scale(1.1) !important;
            transform-origin: left center !important;
            margin-left: 3px !important;
            padding: 0 !important;
          }
          .mobile-rating-wrapper .rating-container {
            transform: none;
                                    padding-bottom: 0px; margin-left: 2px;
          }
          .mobile-rating-wrapper .star {
            font-size: 16px;
          }
          .mobile-rating-wrapper .rating-star,
          .mobile-rating-wrapper .rating-number {
            font-size: 13px ;
            
          }
          .mobile-meta {
            font-size: 13px;
            color: rgba(255, 255, 255, 0.8);
            white-space: nowrap;
            overflow: hidden;
            text-align: right;
            flex: 1 1 auto;
            min-width: 0;
          }
          .mobile-meta-no-ellipsis {
            overflow: visible !important;
            text-overflow: clip !important;
            white-space: nowrap;
          }
        }

        @media (max-width: 480px) {
          .movie-title {
            font-size: 10px;
          }
          .mobile-rating-wrapper .star {
            font-size: 10px !important;
          }
          .mobile-rating-wrapper .rating-star,
          .mobile-rating-wrapper .rating-number {
            font-size: 9px;
          }
          .mobile-meta {
            font-size: 10px;
          }
        }

        @media (max-width: 400px) {
          .movie-title {
            font-size: 10px;
          }
          .mobile-rating-wrapper .star {
            font-size: 10px;
            
          }
          .mobile-rating-wrapper .rating-star,
          .mobile-rating-wrapper .rating-number {
            font-size: 9px;
            
          }
            


          .mobile-meta {
            font-size: 10px;
          }
        }

        @media (max-width: 1200px) {
          .movie-details {
            padding: 8px 0px !important;
          }
          .movie-meta-row {
            justify-content: space-between !important;
          }
        }
      `}</style>
    </div>
  );
};

RecommendedCard.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    name: PropTypes.string,
    vote_average: PropTypes.number.isRequired,
    backdrop_path: PropTypes.string,
    poster_path: PropTypes.string,
    media_type: PropTypes.string
  }).isRequired,
  isMobile: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isFavorite: PropTypes.bool,
  onToggleFavorite: PropTypes.func,
  backdropLogoUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  showTitleOverlay: PropTypes.bool,
  year: PropTypes.string,
  extraInfo: PropTypes.string,
  ratingFormat: PropTypes.string
};

RecommendedCard.defaultProps = {
  ratingFormat: 'stars'
};

export default RecommendedCard;